// R5.2 / R5.7 — RECOMMENDATIONS (spec §4.9), rendered PER ENTRY inside the entry card.
//
// ONE GROUP. `CS-REC-GROUP-A` is the only heading; Group B does not exist and this surface has no slot for it
// (codebook v1.7 deleted it rather than ship it permanently empty — its mandate was not jointly satisfiable with
// §10.5's bans). Kinds are labelled `CS-REC-KIND-*` and are STRICTLY SEPARATED: a known fact is never blended
// with a question, and a suggested phrasing is never presented as a finding.
//
// FIXED KIND ORDER, THEN DOCUMENT ORDER, UNIFORM STYLING (§1.4, T-ORDER-1, T-UNIFORM-1). Every item is the SAME
// item — one style object shared by reference — with no "priority", "focus", "start here", strongest/weakest, or
// emphasis treatment of any recommendation relative to another. Within a kind, the payload's own order is kept
// exactly; nothing is sorted by anything about the applicant.
//
// R5.7 — GROUPED BY ENTRY (live smoke 2026-08-22, Claude+Sol ruling; owner direction: the legacy read grouped
// everything per entry). §4.9's order is unchanged — kind first, then document order — and it now holds INSIDE each
// entry's card: one block per entry, its recommendations partitioned by kind in `REC_KIND_ORDER`, payload order
// within a kind. Document order across entries is the entries section's own order (the read's `entries[]`).
//
// OMISSION SCOPE, WORDED FOR THE SCOPE IT HAS. Every question is a closed-world claim whose `search_scope` is
// PER-ENTRY (`omissionScope` assesses exactly the one entry). Rendering the ratified `CS-OMISSION-SCOPE` for it
// said "assessed across all 1 entries in your CV" — false. A per-entry scope now discloses through the provisional
// `CS-OMISSION-SCOPE-ENTRY`; a genuine CV-wide scope keeps `CS-OMISSION-SCOPE` with its `{n}` (T-COUNT-1's sole
// allowlisted count). ONE disclosure of each kind per entry block, de-duplicated — never deleted (C2b).
//
// SUGGESTED PHRASING IS SHOWN, NEVER INSERTED. There is no apply-to-CV button, no copy-into-editor affordance,
// and no write path of any kind on this surface — the firewall is that the system diagnoses and the applicant
// writes every word. The phrasing itself renders ONLY when `checkPhrasingRenderable` passes the FULL T-AF-2
// seven-condition check against the recommendation's own entry and facts; a failure renders nothing at all,
// because a scaffold we cannot verify is a scaffold that might be inventing a fact.
import type { RubricReadPublicV1 } from '../../../workers/src/lib/rubricReadPublic';
import {
  checkPhrasingRenderable,
  type Recommendation,
  type PhrasingLedgerContext,
} from '../../../workers/src/lib/recommendations';
import type { EntryRecord, LedgerFact } from '../../../workers/src/lib/factLedger';
import { Copy, EvidenceReveal, PhrasingText, placeholderSlot, type TypedSlot } from './renderContent';
import { resolveEvidence, type EvidenceIndex } from './evidenceIndex';
import { BODY_STYLE, MUTED_STYLE, REC_CARD_STYLE, SUB_LABEL_STYLE } from './styles';

type Rec = RubricReadPublicV1['recommendations'][number];

/**
 * The FIXED kind order — Appendix A's own order for the three `CS-REC-KIND-*` ids, and the order §10.3 introduces
 * them in. Pinned as a constant so "fixed kind order" is a value in the code, not a claim in a comment.
 */
export const REC_KIND_ORDER: readonly Rec['kind'][] = ['known_fact', 'question_to_answer', 'suggested_phrasing'];

/** Kind order, then payload (document) order. A STABLE partition — never a comparator over anything else. */
export function orderRecommendations(recs: readonly Rec[]): Rec[] {
  return REC_KIND_ORDER.flatMap((kind) => recs.filter((r) => r.kind === kind));
}

/** The recommendations of ONE entry, in kind order then payload order. */
export function recommendationsForEntry(recs: readonly Rec[], entry_id: string): Rec[] {
  return orderRecommendations(recs.filter((r) => r.entry_id === entry_id));
}

/**
 * Is this closed-world scope the ENTRY'S OWN — exactly this one entry assessed — or a wider (CV-wide / domain)
 * one? Decided from the scope's own `entry_ids_assessed`, never from the claim type: a scope that names exactly
 * the recommendation's entry and nothing else is per-entry; anything else is the wider world it says it is.
 */
export function isEntryScope(rec: Rec): boolean {
  if (rec.evidence.kind !== 'closed_world') return false;
  const ids = rec.evidence.search_scope?.entry_ids_assessed ?? [];
  return ids.length === 1 && ids[0] === rec.entry_id;
}

/** The per-entry ledger context AF-2 needs. Absent for an entry the ledger did not return ⇒ no phrasing renders. */
export type LedgerEntryIndex = ReadonlyMap<string, EntryRecord>;

/**
 * One entry's recommendation block: the registered group label, then the items, then the scope disclosure(s).
 * Renders NOTHING for an entry with no recommendations — no empty group, no placeholder.
 */
/** The recommendation kinds the facts screen reports per entry — the QUESTIONS are the legacy rows (`legacyAdapter.ts`). */
export function nonQuestionRecommendations(recs: readonly Rec[]): Rec[] {
  return recs.filter((r) => r.kind !== 'question_to_answer');
}

export function EntryRecommendations({
  entryId,
  recommendations,
  evidence,
  ledgerEntries,
}: {
  entryId: string;
  recommendations: readonly Rec[];
  evidence: EvidenceIndex;
  ledgerEntries: LedgerEntryIndex;
}) {
  const ordered = recommendationsForEntry(recommendations, entryId);
  if (ordered.length === 0) return null;
  // THE SCOPE DISCLOSURES, CONSOLIDATED: at most one per-entry line and at most one CV-wide line per block. A
  // CV-wide scope keeps its `{n}`; the first such scope speaks for the block (every CV-wide scope on one read
  // assesses the same closed world).
  const perEntry = ordered.some((r) => isEntryScope(r));
  const cvWide = ordered.find(
    (r) => r.evidence.kind === 'closed_world' && !isEntryScope(r) && (r.evidence.search_scope?.n_assessed ?? 0) > 0,
  );
  return (
    <div data-testid="rubric-entry-recommendations">
      <p style={SUB_LABEL_STYLE}>
        <Copy id="CS-REC-GROUP-A" />
      </p>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {ordered.map((rec, i) => (
          <li key={`${rec.kind}-${rec.entry_id}-${i}`} style={REC_CARD_STYLE} data-rec-kind={rec.kind}>
            <p style={{ ...MUTED_STYLE, margin: '0 0 var(--space-1)' }}>
              <Copy id={rec.kind_copy_string_id} />
            </p>
            {rec.copy_string_id && (
              <p style={{ ...BODY_STYLE, margin: 0 }}>
                <Copy id={rec.copy_string_id} />
              </p>
            )}
            {rec.kind === 'suggested_phrasing' && <SuggestedPhrasing rec={rec} ledgerEntries={ledgerEntries} />}
            {rec.evidence.kind === 'facts' && (
              <EvidenceReveal evidence={resolveEvidence(evidence, rec.evidence.fact_ids)} />
            )}
          </li>
        ))}
      </ul>
      {perEntry && (
        <p style={MUTED_STYLE} data-testid="rubric-rec-omission-scope-entry">
          <Copy id="CS-OMISSION-SCOPE-ENTRY" />
        </p>
      )}
      {cvWide && cvWide.evidence.kind === 'closed_world' && (
        <p style={MUTED_STYLE} data-testid="rubric-rec-omission-scope">
          <Copy id="CS-OMISSION-SCOPE" slots={{ n: String(cvWide.evidence.search_scope.n_assessed) }} />
        </p>
      )}
    </div>
  );
}

/**
 * The phrasing scaffold: a registered `PHRASING_TEMPLATES` template with typed slots.
 *
 * TWO PROVENANCES IN ONE SENTENCE, kept apart on purpose (§1.2b). A `ledger` slot's value is the applicant's own
 * stored value — `applicant_verbatim`, exempt from the banned-output sweep, rendered verbatim. A `placeholder`
 * slot's literal is SYSTEM-authored, drawn from the closed Appendix A.4 set, scanned as `copy`, and rendered
 * visibly-placeholder. Emitting the filled sentence as one string would mark the applicant's words as our copy —
 * the laundering §12.6 explicitly looks for — so the renderer segments it and marks each half honestly.
 */
function SuggestedPhrasing({ rec, ledgerEntries }: { rec: Rec; ledgerEntries: LedgerEntryIndex }) {
  const entry = ledgerEntries.get(rec.entry_id);
  if (!entry || rec.template_id === null || rec.slots === null) return null;

  const factsById: ReadonlyMap<string, LedgerFact> = new Map(
    (entry.facts ?? []).map((f: LedgerFact) => [f.fact_id, f]),
  );
  const ctx: PhrasingLedgerContext = { entry, factsById };
  // THE FULL SEVEN-CONDITION CHECK, run against the RECOMMENDATION'S OWN entry — registered template, every slot
  // filled, every ledger slot naming an approved-source fact owned by this entry whose value EQUALS the stored
  // ledger value, every slot fact present in the evidence list, every placeholder literal registered, and
  // traceable evidence at all. Any one failing blocks the render, and the block is silent: an unverifiable
  // scaffold is not shown with a warning, it is not shown.
  const check = checkPhrasingRenderable(rec as Recommendation, ctx);
  if (!check.renderable) return null;

  const slots: Record<string, TypedSlot> = {};
  for (const [name, slot] of Object.entries(rec.slots)) {
    if (slot.kind === 'ledger') {
      slots[name] = { source: 'applicant_verbatim', text: slot.value };
      continue;
    }
    // Placeholder membership, re-checked at the render site. `checkPhrasingRenderable` already refuses an
    // unapproved literal; agreeing here means neither check can be the only thing standing between an arbitrary
    // fabricated sentence and the screen.
    const typed = placeholderSlot(slot.literal);
    if (typed === null) return null;
    slots[name] = typed;
  }

  return (
    <p style={{ ...BODY_STYLE, margin: 'var(--space-1) 0 0' }} data-testid="rubric-suggested-phrasing">
      <PhrasingText templateId={rec.template_id} slots={slots} />
    </p>
  );
}
